import type {ReactElement} from 'react';
import {useRef, useState} from 'react';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {InputField} from '@Components/input-field-v2';
import {updatePreviewTitle} from '@Components/event-wizard/preview/preview.helper';
import {onEventWizardIsAIEventTypeLoading, onEventWizardSelectedTypeChange, onEventWizardTitleChange} from '@Components/event-wizard/event-wizard-slice';
import {useEventWizardShouldCheckErrState, useEventWizardTitle} from '@Components/event-wizard/event-wizard.hooks';
import {isEventNameComplete, NAME_LENGTH_LIMIT} from '@Components/event-wizard/name-input/name-input.types';
import type {EventType} from '@Components/event-wizard/type-dropdown/type-dropdown.types';
import styles from './name-input.module.scss';
import {useAppDispatch} from '@/hooks';
import {EventWizardTestId} from '@/test-ids/event-wizard-test-id';
import {PreviewShortcutTarget} from '@Components/event-wizard/preview/preview.types';
import {usePreviewShortcutClicked} from '@Components/event-wizard/preview/use-preview-shortcut-clicked';

export function NameInput(): ReactElement {
  const dispatch = useAppDispatch();
  const title = useEventWizardTitle();
  const shouldCheckForErr = useEventWizardShouldCheckErrState();

  const [hasTitleChangedSinceGeneratingAIEventType, setHasTitleChangedSinceGeneratingAIEventType] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  usePreviewShortcutClicked(PreviewShortcutTarget.TITLE, (): void => {
    inputRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    inputRef.current?.focus({preventScroll: true});
  });

  const isInErrorState = (): boolean => {
    return shouldCheckForErr && !isEventNameComplete(title);
  };

  const updateTitle = (newTitle: InputType): void => {
    const newTitleStr = newTitle.toString();

    if (newTitleStr.length > NAME_LENGTH_LIMIT) {
      return;
    }
    dispatch(onEventWizardTitleChange(newTitleStr));
    updatePreviewTitle(newTitleStr);
    setHasTitleChangedSinceGeneratingAIEventType(true);
  };

  const aiGenerateEventTypeUsingName = (name: string): void => {
    if (!hasTitleChangedSinceGeneratingAIEventType) {
      return;
    }

    if (name === '') {
      return;
    }
    dispatch(onEventWizardIsAIEventTypeLoading(true));
    void window.PMW.writeLocal('event/getAiGeneratedEventType', {name}).then((aiEventType: EventType) => {
      dispatch(onEventWizardSelectedTypeChange(aiEventType));
      setHasTitleChangedSinceGeneratingAIEventType(false);
      dispatch(onEventWizardIsAIEventTypeLoading(false));
    });
  };

  return (
    <InputField
      ref={inputRef}
      input={title}
      onInputChange={updateTitle}
      onInputBlur={() => {
        aiGenerateEventTypeUsingName(title);
      }}
      inputClassName={`${styles.inputField} ${isInErrorState() ? styles.error : ''} radius-8`}
      inputTestId={EventWizardTestId.EVENT_NAME_INPUT}
      label={window.i18next.t('pmwjs_name')}
      inputType={InputFieldType.TEXT}
      placeholder={window.i18next.t('pmwjs_live_music_sunday_service_webinar')}
      state={isInErrorState() ? InputFieldState.ERROR : InputFieldState.DEFAULT}
      information={isInErrorState() ? window.i18next.t('pmwjs_please_add_event_name') : undefined}
    />
  );
}
